import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const form = e.target;
      const formData = new FormData(form);

      const encodedData = new URLSearchParams(formData).toString();

      const response = await fetch('/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: encodedData,
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus(''), 3000);
        form.reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setTimeout(() => setStatus(''), 3000);
    }
  };

  const inputClasses =
    'w-full px-4 py-3 font-mono text-sm text-gray-200 bg-arcade-panel/70 border border-arcade-cyan/30 rounded focus:outline-none focus:border-arcade-cyan focus:shadow-[0_0_10px_rgba(77,232,244,0.35)] transition-all placeholder:text-gray-500';

  return (
    <section
      id="contact"
      className="min-h-screen relative overflow-hidden bg-arcade-bg crt-scanlines py-16 md:py-20 flex items-center pb-32 md:pb-20"
    >
      {/* faint grid */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.06] pointer-events-none"></div>

      {/* decorative pixels */}
      <div className="absolute top-[10%] right-[11%] w-3 h-3 bg-arcade-green"></div>
      <div className="absolute top-[45%] left-[6%] w-2 h-2 bg-arcade-pink animate-pulse"></div>
      <div className="absolute bottom-[12%] right-[20%] w-2.5 h-2.5 bg-arcade-yellow"></div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 w-full text-center">
        {/* CONTINUE? ▶ YES */}
        <motion.p
          className="font-pixel text-arcade-yellow text-sm md:text-2xl mb-6 md:mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {t('contact.continueLabel')}{' '}
          <span className="inline-block animate-pulse mx-2 md:mx-4">▶</span>{' '}
          {t('contact.continueYes')}
        </motion.p>

        {/* LET'S TEAM UP */}
        <motion.h2
          className="font-pixel text-arcade-cyan text-3xl md:text-6xl mb-8 md:mb-10 drop-shadow-[0_0_14px_rgba(77,232,244,0.5)]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {t('contact.teamUp')}
        </motion.h2>

        <motion.p
          className="font-mono text-sm md:text-lg text-gray-200 leading-8 md:leading-9 max-w-2xl mx-auto mb-10 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
        >
          {t('contact.pitch')}
        </motion.p>

        {/* iletişim bağlantıları */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-3 md:gap-4 mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <a
            href="mailto:contact@nurionacak.dev"
            className="font-pixel text-[9px] md:text-[11px] bg-arcade-cyan text-arcade-bg px-5 py-3 rounded border border-arcade-cyan shadow-[0_0_14px_rgba(77,232,244,0.5)] hover:bg-arcade-cyan/80 transition-colors"
          >
            ▶ E-MAIL
          </a>
          <a
            href="https://linkedin.com/in/nurionacak"
            target="_blank"
            rel="noopener noreferrer"
            className="font-pixel text-[9px] md:text-[11px] text-arcade-cyan border border-arcade-cyan px-5 py-3 rounded hover:bg-arcade-cyan/10 transition-colors"
          >
            LINKEDIN
          </a>
          <a
            href="https://github.com/IRUNBOSS"
            target="_blank"
            rel="noopener noreferrer"
            className="font-pixel text-[9px] md:text-[11px] text-arcade-pink border border-arcade-pink px-5 py-3 rounded hover:bg-arcade-pink/10 transition-colors"
          >
            GITHUB
          </a>
          <a
            href="mailto:contact@nurionacak.dev?subject=CV%20Request%20-%20Nuri%20Onacak"
            className="font-pixel text-[9px] md:text-[11px] text-arcade-yellow border border-arcade-yellow px-5 py-3 rounded hover:bg-arcade-yellow/10 transition-colors"
          >
            {t('hero.cvCta').toUpperCase()}
          </a>
        </motion.div>

        {/* form */}
        <motion.div
          className="max-w-xl mx-auto text-left"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.25 }}
        >
          <form
            onSubmit={handleSubmit}
            className="space-y-5"
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
          >
            <input type="hidden" name="form-name" value="contact" />
            <input type="hidden" name="bot-field" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block font-pixel text-arcade-pink text-[10px] md:text-xs mb-2">
                  {t('contact.nameLabel').toUpperCase()}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder={t('contact.namePlaceholder')}
                />
              </div>
              <div>
                <label className="block font-pixel text-arcade-pink text-[10px] md:text-xs mb-2">
                  {t('contact.emailLabel').toUpperCase()}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>
            </div>
            <div>
              <label className="block font-pixel text-arcade-pink text-[10px] md:text-xs mb-2">
                {t('contact.messageLabel').toUpperCase()}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                className={inputClasses}
                rows="4"
                placeholder={t('contact.messagePlaceholder')}
              ></textarea>
            </div>
            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full font-pixel text-[10px] md:text-xs bg-arcade-cyan text-arcade-bg py-4 rounded border border-arcade-cyan shadow-[0_0_14px_rgba(77,232,244,0.5)] hover:bg-arcade-cyan/80 transition-colors disabled:opacity-50"
            >
              {status === 'sending'
                ? t('contact.sending').toUpperCase()
                : `▶ ${t('contact.sendButton').toUpperCase()}`}
            </button>

            {status === 'success' && (
              <p className="font-mono text-arcade-green text-sm text-center">
                ● {t('contact.successMessage')}
              </p>
            )}

            {status === 'error' && (
              <p className="font-mono text-arcade-pink text-sm text-center">
                ■ {t('contact.errorMessage')}
              </p>
            )}
          </form>
        </motion.div>

        <div className="mt-14 md:mt-16">
          <span className="font-mono text-arcade-purple/50 text-xs tracking-widest select-none">
            © DESIGNED BY IRUNBOSS
          </span>
        </div>
      </div>
    </section>
  );
}

export default Contact;
